import { useCallback, useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { usePageFocus } from "../../hooks/usePageFocus";
import { notify } from "../../lib/notifications";
import { Approval } from "../../components/approvalsTypes";

export const useClientApprovals = (clientId: string) => {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApprovals = useCallback(
    async (status?: string) => {
      try {
        setLoading(true);
        
        let query = supabase
          .from("approval_requests")
          .select(
            `
            id,
            project_id,
            contractor_id,
            timestamp,
            title,
            description,
            type,
            status,
            timeline_impact,
            priority,
            photo_urls,
            client_response,
            client_response_date,
            projects!inner (
              id,
              client_id
            ),
            approval_line_items (
              id,
              description,
              quantity,
              unit_price
            )
          `
          )
          .eq("projects.client_id", clientId);

        if (status) {
          query = query.eq("status", status);
        }

        const { data, error } = await query.order("timestamp", {
          ascending: false,
        });

        if (error) {
          console.error("Error fetching approvals:", error);
          setApprovals([]);
          setLoading(false);
          return [];
        }

        // Transform data to match Approval type
        const transformedData: Approval[] = (data || []).map((item: any) => ({
          id: item.id,
          project_id: item.project_id,
          contractor_id: item.contractor_id,
          timestamp: item.timestamp,
          title: item.title,
          description: item.description,
          type: item.type,
          status: item.status,
          timeline_impact: item.timeline_impact,
          priority: item.priority,
          photo_urls: item.photo_urls,
          client_response: item.client_response,
          client_response_date: item.client_response_date,
          pricing_breakdown: item.approval_line_items || null,
        }));

        setApprovals(transformedData);
        setLoading(false);
        return transformedData;
      } catch (err) {
        console.error("Error in fetchApprovals:", err);
        setApprovals([]);
        setLoading(false);
        return [];
      }
    },
    [clientId]
  );

  const updateApprovalStatus = useCallback(
    async (
      approvalId: string,
      status: "approved" | "rejected" | "discussion",
      clientResponse?: string
    ): Promise<boolean> => {
      try {
        const updateData: Record<string, unknown> = { status };
        if (clientResponse) {
          updateData.client_response = clientResponse;
          updateData.client_response_date = new Date().toISOString();
        }

        const { error } = await supabase
          .from("approval_requests")
          .update(updateData)
          .eq("id", approvalId);

        if (error) {
          console.error("Error updating approval:", error);
          return false;
        }

        // Notify the contractor of the client's response
        const { data: approvalData } = await supabase
          .from("approval_requests")
          .select("contractor_id, project_id, title")
          .eq("id", approvalId)
          .single();

        if (approvalData?.contractor_id) {
          const statusLabels: Record<string, string> = {
            approved: "approved",
            rejected: "rejected",
            discussion: "requested discussion on",
          };
          notify({
            recipientId: approvalData.contractor_id,
            projectId: approvalData.project_id,
            type: `approval_${status}`,
            title: `Approval ${statusLabels[status] ?? status}`,
            message: `Your client ${statusLabels[status] ?? status} the approval request: "${approvalData.title}"`,
            link: `/contractor/projects/${approvalData.project_id}`,
          });
        }

        // Refresh approvals list
        await fetchApprovals();
        return true;
      } catch (err) {
        console.error("Error in updateApprovalStatus:", err);
        return false;
      }
    },
    [fetchApprovals]
  );

  useEffect(() => {
    if (clientId) {
      fetchApprovals();
    }
  }, [clientId, fetchApprovals]);

  usePageFocus(fetchApprovals);

  return {
    approvals,
    loading,
    fetchApprovals,
    updateApprovalStatus,
  };
};
